import { createElement, useEffect, useRef, useState } from 'react';
import { startCapture } from '@/speech/capture/core';
import { micOwner } from '@/speech/capture/mic-owner';
import type { CaptureHandle, PcmFrame } from '@/speech/contracts';
import { encodePcm1, sha256Hex, type ChunkFrame } from '@/workspace/meeting-capture-chunks';
import { voiceApi } from '@/company/voice-api';
import type { VoiceSignatureCopyKey } from '@/i18n/ns/voice-signature';
import { signatureError } from './signature-error';
import {
  elapsedSeconds,
  formatElapsed,
  chunkFrames,
  MAX_ENROLMENT_CHUNKS,
  MAX_ENROLMENT_MS,
  MIN_ENROLMENT_MS,
} from './enrolment-recorder-session';

type EnrolmentHandle = CaptureHandle & { enrolmentId?: string };
export function EnrolmentRecorder({
  onDone,
  copy,
}: {
  onDone: () => void;
  copy: Record<VoiceSignatureCopyKey, string>;
}) {
  const capture = useRef<EnrolmentHandle | null>(null);
  const frames = useRef<ChunkFrame[]>([]);
  const release = useRef<(() => void) | null>(null);
  const mounted = useRef(true);
  const generation = useRef(0);
  const beginPromise = useRef<Promise<void> | null>(null);
  const revoked = useRef(false);
  const cancelRequested = useRef(false);
  const startedAt = useRef<number | null>(null);
  const enrolmentId = useRef<string | null>(null);
  const maxChunks = useRef(MAX_ENROLMENT_CHUNKS);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const processingAbort = useRef<AbortController | null>(null);
  const maxTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const clearTimers = () => {
    if (timer.current) clearInterval(timer.current);
    if (maxTimer.current) clearTimeout(maxTimer.current);
    timer.current = null;
    maxTimer.current = null;
  };
  const clearSession = () => {
    clearTimers();
    capture.current = null;
    enrolmentId.current = null;
    startedAt.current = null;
    frames.current = [];
    release.current?.();
    release.current = null;
    revoked.current = false;
    cancelRequested.current = false;
    setRecording(false);
    setBusy(false);
    setLevel(0);
    setElapsed(0);
  };
  const discard = async (expectedGeneration?: number) => {
    if (expectedGeneration !== undefined && expectedGeneration !== generation.current) return;
    revoked.current = true;
    processingAbort.current?.abort();
    const discardedGeneration = ++generation.current;
    clearTimers();
    release.current?.();
    release.current = null;
    const handle = capture.current;
    capture.current = null;
    try {
      await handle?.stop();
    } catch {
      /* device release is best effort; never upload */
    } finally {
      if (generation.current === discardedGeneration) clearSession();
    }
  };
  const waitForProcessing = async (id: string, signal: AbortSignal) => {
    for (let attempt = 0; attempt < 60; attempt += 1) {
      signal.throwIfAborted();
      const status = await voiceApi.enrolmentStatus(id);
      signal.throwIfAborted();
      if (status.status === 'done') return;
      if (status.status === 'failed') throw new Error(status.failureCode ?? copy.unavailable);
      await new Promise<void>((resolve, reject) => {
        const cancel = () => {
          clearTimeout(delay);
          reject(signal.reason);
        };
        const delay = setTimeout(() => {
          signal.removeEventListener('abort', cancel);
          resolve();
        }, 1000);
        signal.addEventListener('abort', cancel, { once: true });
      });
    }
    throw new Error(copy.unavailable);
  };
  const stop = async (userInitiated: boolean) => {
    if (!capture.current) {
      if (!userInitiated) cancelRequested.current = true;
      return;
    }
    const stopGeneration = generation.current;
    const ageMs = Date.now() - (startedAt.current ?? Date.now());
    if (userInitiated && ageMs < MIN_ENROLMENT_MS) return;
    const handle = capture.current;
    const id = enrolmentId.current;
    if (!handle || !id) return discard();
    clearTimers();
    capture.current = null;
    setBusy(true);
    try {
      await handle.stop();
      if (
        !userInitiated ||
        revoked.current ||
        !mounted.current ||
        generation.current !== stopGeneration
      )
        return;
      const chunks = chunkFrames(frames.current, maxChunks.current);
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
        if (!mounted.current || revoked.current || generation.current !== stopGeneration) return;
        const encoded = encodePcm1({
          take: 0,
          chunkIndex,
          final: chunkIndex === chunks.length - 1,
          frames: chunks[chunkIndex],
        });
        await voiceApi.uploadChunk(id, chunkIndex, encoded.bytes, await sha256Hex(encoded.bytes));
      }
      if (!mounted.current || revoked.current || generation.current !== stopGeneration) return;
      const status = await voiceApi.finishEnrolment(id);
      if (status.status === 'processing' || status.status === 'receiving') {
        const pending = new AbortController();
        processingAbort.current = pending;
        if (!mounted.current || revoked.current || generation.current !== stopGeneration)
          pending.abort();
        await waitForProcessing(id, pending.signal);
      } else if (status.status === 'failed')
        throw new Error(status.failureCode ?? copy.unavailable);
      else if (status.status !== 'done') throw new Error(copy.unavailable);
      if (mounted.current && generation.current === stopGeneration) onDone();
    } catch (e) {
      if (mounted.current && generation.current === stopGeneration)
        setError(signatureError(e, copy));
    } finally {
      if (generation.current === stopGeneration) clearSession();
    }
  };
  const begin = async () => {
    if (!mounted.current || recording || busy || beginPromise.current) return;
    const claimGeneration = ++generation.current;
    revoked.current = false;
    cancelRequested.current = false;
    const claim = micOwner.claim('enrolment', () => void discard(claimGeneration));
    if (!claim) {
      setError(copy.unavailable);
      return;
    }
    release.current = claim;
    setBusy(true);
    setError(null);
    const operation = (async () => {
      try {
        const enrolment = await voiceApi.startEnrolment({ kind: 'recording' });
        if (
          !mounted.current ||
          claimGeneration !== generation.current ||
          revoked.current ||
          cancelRequested.current
        ) {
          await discard(claimGeneration);
          return;
        }
        const handle = (await startCapture({
          sources: ['mic'],
          purpose: 'enrolment',
          onFrame: (frame: PcmFrame) => {
            if (
              mounted.current &&
              !revoked.current &&
              claimGeneration === generation.current &&
              frames.current.length < MAX_ENROLMENT_CHUNKS * 100
            )
              frames.current.push({ seq: frame.seq, pcm: frame.pcm });
          },
          onLevel: (value) => {
            if (mounted.current) setLevel(value);
          },
        })) as EnrolmentHandle;
        if (
          !mounted.current ||
          claimGeneration !== generation.current ||
          revoked.current ||
          cancelRequested.current
        ) {
          await handle.stop().catch(() => {});
          await discard(claimGeneration);
          return;
        }
        handle.enrolmentId = enrolment.enrolmentId;
        capture.current = handle;
        enrolmentId.current = enrolment.enrolmentId;
        maxChunks.current = Math.min(
          MAX_ENROLMENT_CHUNKS,
          Math.max(
            1,
            'maxChunks' in enrolment && typeof enrolment.maxChunks === 'number'
              ? enrolment.maxChunks
              : MAX_ENROLMENT_CHUNKS,
          ),
        );
        startedAt.current = Date.now();
        setElapsed(0);
        setRecording(true);
        setBusy(false);
        timer.current = setInterval(() => {
          if (startedAt.current) setElapsed(elapsedSeconds(startedAt.current));
        }, 250);
        maxTimer.current = setTimeout(() => {
          if (claimGeneration !== generation.current) return;
          setError(copy.errorExpired);
          void discard(claimGeneration);
        }, MAX_ENROLMENT_MS);
      } catch (e) {
        if (mounted.current) setError(signatureError(e, copy));
        await discard(claimGeneration);
      }
    })();
    beginPromise.current = operation;
    try {
      await operation;
    } finally {
      if (beginPromise.current === operation) beginPromise.current = null;
    }
  };
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      processingAbort.current?.abort();
      generation.current += 1;
      revoked.current = true;
      clearTimers();
      const handle = capture.current;
      capture.current = null;
      frames.current = [];
      release.current?.();
      release.current = null;
      void handle?.stop().catch(() => {});
    };
  }, []);
  return recording
    ? createElement(
        'div',
        // voice-structural: React DOM class and test-id attributes, not routing data.
        { className: 'company-form', 'data-testid': 'voice-enrolment-recording' },
        createElement('p', null, copy.passage),
        createElement(
          'p',
          { 'data-testid': 'voice-enrolment-elapsed' },
          busy ? copy.processing : `${copy.recording} ${formatElapsed(elapsed)}`,
        ),
        error ? createElement('p', { role: 'alert' }, error) : null,
        createElement('progress', { max: 1, value: level, 'aria-label': copy.recording }),
        createElement(
          'button',
          {
            className: 'company-primary',
            disabled: busy || elapsed * 1000 < MIN_ENROLMENT_MS,
            onClick: () => void stop(true),
          },
          copy.stop,
        ),
      )
    : createElement(
        'div',
        { className: 'company-form' },
        error ? createElement('p', { role: 'alert' }, error) : null,
        createElement(
          'button',
          { className: 'company-primary', disabled: busy, onClick: () => void begin() },
          copy.recordStart,
        ),
      );
}
